import React, {useState} from "react";
import {FormattedMessage} from "react-intl";
import classes from "./styles.module.css";

const EVENT_TYPE_LABELS = {
    MERCHANT_REQUEST: {ru: "Запрос от мерчанта", en: "Merchant Request", icon: "📥", color: "#35C98E"},
    MERCHANT_RESPONSE: {ru: "Ответ мерчанту", en: "Merchant Response", icon: "📤", color: "#2479E0"},
    MERCHANT_CALLBACK: {ru: "Callback мерчанту", en: "Callback to Merchant", icon: "🔔", color: "#9c27b0"},
    MERCHANT_CALLBACK_MISSING: {ru: "Callback не настроен", en: "Callback Missing", icon: "⚠️", color: "#ff9800"},
    PROVIDER_REQUEST: {ru: "Запрос к провайдеру", en: "Provider Request", icon: "➡️", color: "#2479E0"},
    PROVIDER_RESPONSE: {ru: "Ответ от провайдера", en: "Provider Response", icon: "⬅️", color: "#35C98E"},
    PROVIDER_CALLBACK: {ru: "Callback от провайдера", en: "Provider Callback", icon: "📨", color: "#35C98E"},
    PROVIDER_EVENT: {ru: "Событие провайдера", en: "Provider Event", icon: "📋", color: "#607d8b"},
    STATUS_CHANGE: {ru: "Изменение статуса", en: "Status Change", icon: "🔄", color: "#ff9800"},
    FRAUD_CHECK: {ru: "Проверка антифрода", en: "Fraud Check", icon: "🛡️", color: "#607d8b"},
    FRAUD_BLOCK: {ru: "Блокировка антифродом", en: "Fraud Block", icon: "🚫", color: "#f44336"},
    ADMIN_ACTION: {ru: "Действие админа", en: "Admin Action", icon: "👤", color: "#673ab7"},
    SYSTEM_NOTE: {ru: "Системная заметка", en: "System Note", icon: "📝", color: "#9e9e9e"},
};

const DIRECTION_STYLES = {
    IN: {icon: "arrow-right", color: "#35C98E"},
    OUT: {icon: "arrow-left", color: "#2479E0"},
};

function TimelineEvent({event, isLast}) {
    const [expanded, setExpanded] = useState(false);
    const [copied, setCopied] = useState(false);

    const {
        eventType,
        direction,
        actor,
        statusFrom,
        statusTo,
        httpCode,
        errorCode,
        errorMessage,
        payload,
        meta,
        clientIp,
        createdAt,
    } = event;

    const label = EVENT_TYPE_LABELS[eventType] || {ru: eventType, en: eventType, icon: "📋", color: "#9e9e9e"};
    const dirStyle = DIRECTION_STYLES[direction] || {};
    const time = createdAt ? new Date(createdAt).toLocaleTimeString() : "";
    const date = createdAt ? new Date(createdAt).toLocaleDateString() : "";

    const hasDetails = payload || meta || errorMessage;

    // Извлекаем callbackUrl из meta для MERCHANT_CALLBACK
    const callbackUrl = (meta && meta.callbackUrl) || null;

    // Функция копирования
    const handleCopy = (data) => {
        navigator.clipboard.writeText(JSON.stringify(data, null, 2));
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    // Определяем цвет точки: приоритет у типа события, затем направление
    const dotColor = label.color || dirStyle.color || "#9e9e9e";

    return (
        <div className={classes.EventContainer}>
            <div className={classes.EventLine}>
                <div className={classes.EventDot} style={{backgroundColor: dotColor}}/>
                {!isLast && <div className={classes.EventConnector}/>}
            </div>

            <div className={classes.EventContent}>
                <div className={classes.EventHeader} onClick={() => hasDetails && setExpanded(!expanded)}>
                    <div className={classes.EventTime}>
                        <span className={classes.Time}>{time}</span>
                        <span className={classes.Date}>{date}</span>
                    </div>

                    <div className={classes.EventTitle}>
                        <span className={classes.EventIcon}>{label.icon}</span>
                        <span className={classes.EventLabel}>{label.ru}</span>
                        {direction && (
                            <span
                                className={classes.DirectionBadge}
                                style={{backgroundColor: dirStyle.color}}
                            >
                                {direction}
                            </span>
                        )}
                        {actor && actor !== "system" && actor !== "merchant" && (
                            <span className={classes.ActorBadge}>{actor}</span>
                        )}
                    </div>

                    {httpCode !== null && httpCode !== undefined && (
                        <span className={httpCode >= 200 && httpCode < 300 ? classes.HttpOk : classes.HttpError}>
                            HTTP {httpCode}
                        </span>
                    )}

                    {hasDetails && (
                        <span className={classes.ExpandIcon}>{expanded ? "▼" : "▶"}</span>
                    )}
                </div>

                {/* Показываем callbackUrl для MERCHANT_CALLBACK */}
                {callbackUrl && (
                    <div className={classes.CallbackUrl}>
                        <span className={classes.CallbackLabel}>Адрес:</span>
                        <span className={classes.CallbackValue}>{callbackUrl}</span>
                    </div>
                )}

                {eventType === "STATUS_CHANGE" && (
                    <div className={classes.StatusChange}>
                        <span className={classes.StatusFrom}>{statusFrom || "?"}</span>
                        <span className={classes.StatusArrow}>→</span>
                        <span className={classes.StatusTo}>{statusTo || "?"}</span>
                    </div>
                )}

                {errorCode && (
                    <div className={classes.ErrorInfo}>
                        <span className={classes.ErrorCode}>{errorCode}</span>
                        {errorMessage && <span className={classes.ErrorMessage}>{errorMessage}</span>}
                    </div>
                )}

                {clientIp && (
                    <div className={classes.ClientIp}>IP: {clientIp}</div>
                )}

                {expanded && hasDetails && (
                    <div className={classes.Details}>
                        {meta && Object.keys(meta).length > 0 && (
                            <div className={classes.MetaSection}>
                                <div className={classes.SectionHeader}>
                                    <span className={classes.SectionTitle}>
                                        <FormattedMessage id="TransactionAudit.meta" defaultMessage="Детали"/>
                                    </span>
                                    <button
                                        className={classes.CopyButton}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleCopy(meta);
                                        }}
                                        title="Копировать"
                                    >
                                        {copied ? "✓" : "📋"}
                                    </button>
                                </div>
                                <pre className={classes.JsonPre}>
                                    {JSON.stringify(meta, null, 2)}
                                </pre>
                            </div>
                        )}
                        {payload && Object.keys(payload).length > 0 && (
                            <div className={classes.PayloadSection}>
                                <div className={classes.SectionHeader}>
                                    <span className={classes.SectionTitle}>
                                        <FormattedMessage id="TransactionAudit.payload" defaultMessage="Данные"/>
                                    </span>
                                    <button
                                        className={classes.CopyButton}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleCopy(payload);
                                        }}
                                        title="Копировать"
                                    >
                                        {copied ? "✓" : "📋"}
                                    </button>
                                </div>
                                <pre className={classes.JsonPre}>
                                    {JSON.stringify(payload, null, 2)}
                                </pre>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default TimelineEvent;



// WEBPACK FOOTER //
// ./src/Components/TransactionAudit/TimelineEvent.js